import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/es";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

export { dayjs };

export function nowInTz(tz?: string): dayjs.Dayjs {
  return tz ? dayjs().tz(tz) : dayjs();
}

export function parseInTz(input: string, format: string, tz?: string): dayjs.Dayjs {
  return tz ? dayjs.tz(input, format, tz) : dayjs(input, format);
}

export function todayStr(tz?: string): string {
  return nowInTz(tz).format("YYYY-MM-DD");
}

/** Human-friendly date label for booking surfaces. Returns
 *  ``"Hoy, 26 may 2026"`` when the date is today, ``"Mañana, …"``
 *  for tomorrow, and ``"Jueves, …"`` for any other day. Uses the
 *  optional ``tz`` so "today" matches the location's wall clock and
 *  not the operator's browser timezone. */
export function formatBookingDateLabel(date: string, tz?: string): string {
  const target = (tz ? dayjs.tz(date, tz) : dayjs(date)).startOf("day");
  const today = (tz ? nowInTz(tz) : dayjs()).startOf("day");
  const diffDays = target.diff(today, "day");
  const dateStr = target.format("D MMM YYYY");
  if (diffDays === 0) return `Hoy, ${dateStr}`;
  if (diffDays === 1) return `Mañana, ${dateStr}`;
  const dayName = target.format("dddd");
  return `${dayName.charAt(0).toUpperCase()}${dayName.slice(1)}, ${dateStr}`;
}

export function formatTime(d: dayjs.Dayjs): string {
  let h = d.hour();
  const m = d.minute().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

export function nowFormatted(tz?: string): string {
  return formatTime(nowInTz(tz));
}

export function parseTime(time: string): dayjs.Dayjs {
  const match = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) {
    return dayjs();
  }
  let h = parseInt(match[1]);
  const m = parseInt(match[2]);
  const ampm = match[3].toUpperCase();
  if (ampm === "PM" && h !== 12) {
    h += 12;
  }
  if (ampm === "AM" && h === 12) {
    h = 0;
  }
  return dayjs().hour(h).minute(m).second(0).millisecond(0);
}

export function addHoursToTime(time: string, hours: number): string {
  return formatTime(parseTime(time).add(hours, "hour"));
}

export function timeFromNow(minutesDelta: number): string {
  const d = dayjs().add(minutesDelta, "minute");
  const rounded = Math.round(d.minute() / 5) * 5;
  return formatTime(d.minute(rounded).second(0).millisecond(0));
}

export function parseTimeHour(time: string): number {
  const m = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) {
    return 12;
  }
  let h = parseInt(m[1]);
  const min = parseInt(m[2]);
  if (m[3].toUpperCase() === "PM" && h !== 12) {
    h += 12;
  }
  if (m[3].toUpperCase() === "AM" && h === 12) {
    h = 0;
  }
  return h + min / 60;
}

export function formatCurrentTime(d: dayjs.Dayjs): string {
  let h = d.hour();
  const m = d.minute().toString().padStart(2, "0");
  h = h % 12 || 12;
  return `${h}:${m}`;
}

/** Resolve a reservation time string to a real dayjs instant.
 *  When ``date`` (YYYY-MM-DD) is provided the result is anchored there;
 *  otherwise it falls back to the legacy "pin to today" behaviour for
 *  callers that don't have a date in scope. The today-only fallback
 *  collapses 12h+ rollovers onto the nearest occurrence — a guard for
 *  CI runners and any caller that genuinely cares only about clock
 *  position, not calendar day. */
function _resolveInstant(time: string, date?: string): dayjs.Dayjs {
  if (date) return dayjs(`${date} ${time}`, "YYYY-MM-DD h:mm A");
  return parseTime(time);
}

/** Calculate remaining minutes between now and a reservation time.
 *  Pass ``date`` (the reservation's YYYY-MM-DD) so the math anchors to
 *  the actual calendar day — without it the badge fires on any non-today
 *  view because ``parseTime`` pins to today. The legacy today-only
 *  fallback rolls 12h+ deltas back onto the nearest occurrence. */
export function minutesRemaining(endTime: string, date?: string): number {
  const now = dayjs();
  const end = _resolveInstant(endTime, date);
  let diffMin = Math.round((end.valueOf() - now.valueOf()) / 60_000);
  if (!date) {
    const HALF_DAY_MIN = 12 * 60;
    const DAY_MIN = 24 * 60;
    if (diffMin > HALF_DAY_MIN) {
      diffMin -= DAY_MIN;
    } else if (diffMin < -HALF_DAY_MIN) {
      diffMin += DAY_MIN;
    }
  }
  return Math.max(0, diffMin);
}

/** Format remaining minutes as "Xh YYm" or "Xm" */
export function formatRemainingTime(endTime: string, date?: string): string {
  const diffMin = minutesRemaining(endTime, date);
  const h = Math.floor(diffMin / 60);
  const m = diffMin % 60;
  return h > 0 ? `${h}h${m.toString().padStart(2, "0")}m` : `${m}m`;
}

/** Minutes remaining before a reservation is marked no-show.
 *  Returns a positive number (grace time left), 0 (grace expired), or
 *  ``null`` when the reservation time hasn't arrived yet. Pass ``date``
 *  so the comparison anchors to the reservation's calendar day — without
 *  it, callers viewing a non-today date can trip the overdue branch
 *  using today's clock. */
export function minutesUntilNoShow(
  reservationTime: string,
  toleranceMinutes: number,
  date?: string,
): number | null {
  const now = dayjs();
  const start = _resolveInstant(reservationTime, date);
  const elapsed = Math.round((now.valueOf() - start.valueOf()) / 60_000);
  if (elapsed < 0) return null; // hasn't started yet
  return Math.max(0, toleranceMinutes - elapsed);
}

/** Parse a time string to a native Date (for canvas engine compatibility) */
export function parseTimeToDate(time: string): Date {
  const match = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return new Date();
  let h = parseInt(match[1]);
  const m = parseInt(match[2]);
  const ampm = match[3].toUpperCase();
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

/** Get week days starting from Monday for a given week offset */
export function getWeekDays(offset: number) {
  const monday = dayjs().startOf("week").add(1, "day").add(offset, "week");
  return Array.from({ length: 7 }, (_, i) => {
    const d = monday.add(i, "day");
    return {
      short: DAY_NAMES[d.day()],
      num: d.date(),
      month: d.month(),
      year: d.year(),
      date: d.format("YYYY-MM-DD"),
    };
  });
}

/** Like timeFromNow but rounds to 15 min intervals (for simulation) */
export function timeFromNowRounded15(minutesDelta: number): string {
  const d = dayjs().add(minutesDelta, "minute");
  const rounded = Math.round(d.minute() / 15) * 15;
  return formatTime(d.minute(rounded).second(0).millisecond(0));
}

/** Format a timestamp (ms) to HH:mm */
export function formatTimestamp(ts: number): string {
  return dayjs(ts).format("HH:mm");
}

/** Format a date string for display (e.g., "Lunes 14 Abril 2026") */
export function formatDateLong(dateStr: string): string {
  const d = dayjs(dateStr);
  return `${DAY_NAMES_LONG[d.day()]} ${d.date()} ${MONTH_NAMES[d.month()]} ${d.year()}`;
}

export const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export const DAY_NAMES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const DAY_NAMES_LONG = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
